import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";
import AppUtil from "utilities/AppUtil";
import httpRequest from "../../services/httpRequest";
import snakecaseKeys from "snakecase-keys";

// Main URL Endpoint
const _urlPrefix = "/client";

export const reduceSignupClient = createAsyncThunk(
  "client/reduceSignupClient",
  async (params) => {
    const result = await httpRequest({
      url: _urlPrefix + "/sign-up-id-password",
      method: "POST",
      data: AppUtil.toSnakeCaseKey(params),
    });
    if (!result) {
      return;
    }
    return result.data;
  },
);
export const reduceSignupGoogleClient = createAsyncThunk(
  "client/reduceSignupGoogleClient",
  async (params) => {
    const result = await httpRequest({
      url: _urlPrefix + "/sign-up-google",
      method: "POST",
      data: AppUtil.toSnakeCaseKey(params),
    });
    if (!result) {
      return;
    }
    return result.data;
  },
);
export const reduceSignupFacebookClient = createAsyncThunk(
  "client/reduceSignupFacebookClient",
  async (params) => {
    const result = await httpRequest({
      url: _urlPrefix + "/sign-up-facebook",
      method: "POST",
      data: AppUtil.toSnakeCaseKey(params),
    });
    if (!result) {
      return;
    }
    return result.data;
  },
);
export const updateAgreement = createAsyncThunk(
  "client/updateAgreement",
  async (params) => {
    const { id, companyId, files } = params;
    const data = new FormData();
    if (files) {
      for (let i = 0; i < files.length; i++) {
        data.append("files ", files[i]);
      }
    }

    const result = await httpRequest({
      url: `${_urlPrefix}/update/agreement/${id}/${companyId}`,
      method: "PUT",
      data: data,
      successMessage: "toast.update.200",
    });
    if (!result) {
      return;
    }
    return result.data;
  },
);
export const updatePersonalInfo = createAsyncThunk(
  "client/updatePersonalInfo",
  async (params) => {
    const result = await httpRequest({
      url: `${_urlPrefix}/update/personal-info`,
      method: "PUT",
      data: snakecaseKeys(params),
      successMessage: "toast.update.200",
    });
    if (!result) {
      return;
    }
    // const { organizationName } = result.data;
    // dispatch(updateOrganizationName({ organizationName }));
    return result.data;
  },
);

export const updateClientProfile = createAsyncThunk(
  "client/updateClientProfile",
  async (params) => {
    const result = await httpRequest({
      url: `${_urlPrefix}/update-profile`,
      method: "PUT",
      data: snakecaseKeys(params),
      successMessage: "toast.update.200",
    });
    if (!result) {
      return;
    }
    return result.data;
  },
);
export const updateBillingInfo = createAsyncThunk(
  "client/updateBillingInfo",
  async (params) => {
    const result = await httpRequest({
      url: `${_urlPrefix}/update/billing-info`,
      method: "PUT",
      data: AppUtil.toSnakeCaseKey(params),
      successMessage: "toast.update.200",
    });
    if (!result) {
      return;
    }
    return result.data;
  },
);
export const updateVerification = createAsyncThunk(
  "client/updateVerification",
  async (params) => {
    const {
      clientId,
      // ndisPlanNumber,
      companyId,
      certificationFile,
      bspPlanFile,
      detailVerification,
    } = params;
    const data = new FormData();

    certificationFile && data.append("certification_file ", certificationFile);
    bspPlanFile && data.append("bsp_plan_file ", bspPlanFile);
    data.append(
      "content-data",
      new Blob(
        [
          JSON.stringify(
            snakecaseKeys({
              clientId,
              companyId,
              // ndisPlanNumber,
              detailVerification,
            }),
          ),
        ],
        {
          type: "application/json",
        },
      ),
    );
    const result = await httpRequest({
      url: `${_urlPrefix}/update/verification`,
      method: "PUT",
      headers: {
        "Content-Type": "multipart/form-data",
      },
      data: data,
      successMessage: "toast.update.200",
    });
    if (!result) {
      return;
    }
    return result.data;
  },
);
export const fetchClientDetail = createAsyncThunk(
  "client/fetchClientDetail",
  async (params) => {
    const { clientId, companyId } = params;
    const result = await httpRequest({
      url: `${_urlPrefix}/detail/${clientId}/${companyId}`,
      method: "GET",
    });
    if (!result) {
      return;
    }
    return result.data;
  },
);

export const resendIntakeLink = createAsyncThunk(
  "client/resendIntakeLink",
  async (clientId) => {
    const result = await httpRequest({
      url: `${_urlPrefix}/intake-link/resend/${clientId}`,
      method: "POST",
      successMessage: "Intake link resent successfully.",
    });
    if (!result) return;
    return result.data;
  }
);

export const getPublicIntakeLinkInfo = createAsyncThunk(
  "client/getPublicIntakeLinkInfo",
  async (token) => {
    const result = await httpRequest({
      url: `${_urlPrefix}/public/intake-link/${token}`,
      method: "GET",
    });
    if (!result) return;
    return result.data;
  }
);

export const getIntakeLinkAgreementConsentItem = createAsyncThunk(
  "client/getIntakeLinkAgreementConsentItem",
  async (token) => {
    const result = await httpRequest({
      url: `${_urlPrefix}/public/intake-link/agreement-consent-item`,
      method: "GET",
      params: { intakeLinkToken: token },
    });
    return result.data;
  }
);

export const submitIntakeLinkInfoDetails = createAsyncThunk(
  "client/submitIntakeLinkInfoDetails",
  async (params) => {
    const result = await httpRequest({
      url: `${_urlPrefix}/public/intake-link/info-details`,
      method: "PUT",
      data: snakecaseKeys(params),
      successMessage: "Information details saved successfully",
    });
    if (!result) return;
    return result.data;
  }
);

export const submitIntakeLinkNdisDetails = createAsyncThunk(
  "client/submitIntakeLinkNdisDetails",
  async (formData) => {
    const result = await httpRequest({
      url: `${_urlPrefix}/public/intake-link/ndis-details`,
      method: "PUT",
      data: formData,
      headers: { "Content-Type": "multipart/form-data" },
      successMessage: "NDIS details saved successfully",
    });
    if (!result) return;
    return result.data;
  }
);

export const submitIntakeLinkSupportNeeds = createAsyncThunk(
  "client/submitIntakeLinkSupportNeeds",
  async (params) => {
    const result = await httpRequest({
      url: `${_urlPrefix}/public/intake-link/support-needs`,
      method: "PUT",
      data: snakecaseKeys(params),
      successMessage: "Support needs saved successfully",
    });
    if (!result) return;
    return result.data;
  }
);

  export const submitIntakeLinkContacts = createAsyncThunk(
  "client/submitIntakeLinkContacts",
  async (params) => {
    const result = await httpRequest({
      url: `${_urlPrefix}/public/intake-link/contacts`,
      method: "PUT",
      data: snakecaseKeys(params),
      successMessage: "Contacts saved successfully",
    });
    if (!result) return;
    return result.data;
  }
);

export const submitIntakeLinkDocument = createAsyncThunk(
  "client/submitIntakeLinkDocument",
  async (formData) => {
    const result = await httpRequest({
      url: `${_urlPrefix}/public/intake-link/document`,
      method: "PUT",
      data: formData,
      headers: { "Content-Type": "multipart/form-data" },
      successMessage: "Documents saved successfully",
    });
    if (!result) return;
    return result.data;
  }
);

export const submitIntakeLinkAgreementConsent = createAsyncThunk(
  "client/submitIntakeLinkAgreementConsent",
  async (formData) => {
    const result = await httpRequest({
      url: `${_urlPrefix}/public/intake-link/agreement-consent`,
      method: "PUT",
      data: formData,
      headers: { "Content-Type": "multipart/form-data" },
      successMessage: "Service agreement and consent submitted successfully",
    });
    if (!result) return;
    return result.data;
  }
);
export const updateCarePlan = createAsyncThunk(
  "client/updateCarePlan",
  async (params) => {
    const result = await httpRequest({
      url: `${_urlPrefix}/update/care-plan`,
      method: "PUT",
      data: snakecaseKeys(params),
      successMessage: "toast.update.200",
    });
    if (!result) return;
    return result.data;
  }
);

export const fetchClientListPaging = createAsyncThunk(
  "client/fetchClientListPaging",
  async (params) => {
    const result = await httpRequest({
      url: _urlPrefix,
      method: "GET",
      params: AppUtil.toSnakeCaseKey(params),
    });
    if (!result) {
      return;
    }
    return result.data;
  },
);
export const fetchClientList = createAsyncThunk(
  "client/fetchClientList",
  async (params) => {
    const result = await httpRequest({
      url: `${_urlPrefix}/list/${params}`,
      method: "GET",
    });
    if (!result) {
      return;
    }
    return result.data;
  },
);
export const createClient = createAsyncThunk(
  "client/createClient",
  async (params) => {
    const result = await httpRequest({
      url: `${_urlPrefix}/create`,
      method: "POST",
      data: snakecaseKeys(params),
      successMessage: "toast.create.200",
    });
    if (!result) {
      return;
    }
    return result.data;
  },
);
export const activeClient = createAsyncThunk(
  "client/activeClient",
  async (params) => {
    const { clientId } = params;
    const result = await httpRequest({
      url: `${_urlPrefix}/${clientId}`,
      method: "PUT",
      successMessage: "toast.update.200",
    });
    if (!result) {
      return;
    }
    return result.data;
  },
);
export const verifyClient = createAsyncThunk(
  "client/verifyClient",
  async (params) => {
    const result = await httpRequest({
      url: `${_urlPrefix}/verify-on-board`,
      method: "PUT",
      data: AppUtil.toSnakeCaseKey(params),
      successMessage: "toast.update.200",
    });
    if (!result) {
      return;
    }
    return result.data;
  },
);
export const reduceActivateClient = createAsyncThunk(
  "client/reduceActivateClient",
  async (clientId) => {
    const result = await httpRequest({
      url: `${_urlPrefix}/${clientId}/activate`,
      method: "PUT",
      successMessage: "toast.activate_client.200",
    });
    if (!result) {
      return;
    }
    return result.data;
  },
);

export const activateApprovalClient = createAsyncThunk(
  "client/activateClientUser",
  async (clientId) => {
    const result = await httpRequest({
      url: `${_urlPrefix}/${clientId}/approval-status-active`,
      method: "PUT",
      successMessage: "toast.activate_client.200",
    });
    if (!result) {
      return;
    }
    return result.data;
  },
);

export const reduceDeActivateClient = createAsyncThunk(
  "client/reduceDeActivateClient",
  async (clientId) => {
    const result = await httpRequest({
      url: `${_urlPrefix}/${clientId}/deactivate`,
      method: "PUT",
      successMessage: "toast.de_activate_client.200",
    });
    if (!result) {
      return;
    }
    return result.data;
  },
);
export const fetchClientFeedback = createAsyncThunk(
  "client/fetchClientFeedback",
  async (params) => {
    const { clientId, companyId } = params;
    const result = await httpRequest({
      url: `${_urlPrefix}/on-board-feedbacks/${clientId}/${companyId}`,
      method: "GET",
    });
    if (!result) {
      return;
    }
    return result.data;
  },
);
export const deleteClient = createAsyncThunk(
  "client/deleteClient",
  async (params) => {
    const result = await httpRequest({
      url: `${_urlPrefix}/delete`,
      data: AppUtil.toSnakeCaseKey(params),
      method: "DELETE",
      successMessage: "toast.remove.200",
    });
    if (!result) {
      return;
    }
    return result.data;
  },
);

export const deleteAgreement = createAsyncThunk(
  "client/deleteAgreement",
  async (params) => {
    const { agreementId, companyId } = params;
    const result = await httpRequest({
      url: `${_urlPrefix}/delete-agreement/${agreementId}`,
      params: AppUtil.toSnakeCaseKey({ companyId }),
      method: "DELETE",
      successMessage: "toast.remove.200",
    });
    if (!result) {
      return;
    }
    return result.data;
  },
);

export const reduceUpdateClientNotifyEmail = createAsyncThunk(
  "client/reduceUpdateClientNotifyEmail",
  async (params) => {
    const result = await httpRequest({
      url: `${_urlPrefix}/update/notify-email`,
      data: AppUtil.toSnakeCaseKey(params),
      method: "PUT",
      successMessage: "toast.update.200",
    });
    if (!result) {
      return;
    }
    return result.data;
  },
);

// ==========================================
// Risk & Consent APIs
// ==========================================

export const createOrUpdateRisk = createAsyncThunk(
  "client/createOrUpdateRisk",
  async (data) => {
    const result = await httpRequest({
      url: `${_urlPrefix}/risk`,
      method: "POST",
      data: snakecaseKeys(data),
      successMessage: "toast.add.200",
    });
    if (!result) return;
    return result.data;
  }
);

export const fetchRisksForClient = createAsyncThunk(
  "client/fetchRisksForClient",
  async ({ clientId }) => {
    const result = await httpRequest({
      url: `${_urlPrefix}/risk/${clientId}`,
      method: "GET",
    });
    if (!result) return;
    return result.data;
  }
);

export const fetchRiskOwners = createAsyncThunk(
  "client/fetchRiskOwners",
  async ({ companyId }) => {
    const result = await httpRequest({
      url: `${_urlPrefix}/owners/${companyId}`,
      method: "GET",
    });
    if (!result) return;
    return result.data;
  }
);

export const createOrUpdateConsent = createAsyncThunk(
  "client/createOrUpdateConsent",
  async (params) => {
    const { documentFile, ...requestData } = params;
    const data = new FormData();

    if (documentFile) {
      data.append("document_file", documentFile);
    }

    data.append(
      "content-data",
      new Blob(
        [JSON.stringify(snakecaseKeys(requestData))],
        { type: "application/json" }
      )
    );

    const result = await httpRequest({
      url: `${_urlPrefix}/consent`,
      method: "POST",
      headers: { "Content-Type": "multipart/form-data" },
      data: data,
      successMessage: "toast.add.200",
    });
    if (!result) return;
    return result.data;
  }
);

export const fetchConsentsForClient = createAsyncThunk(
  "client/fetchConsentsForClient",
  async ({ clientId }) => {
    const result = await httpRequest({
      url: `${_urlPrefix}/consent/${clientId}`,
      method: "GET",
    });
    if (!result) return;
    return result.data;
  }
);

export const deleteConsent = createAsyncThunk(
  "client/deleteConsent",
  async ({ id }) => {
    const result = await httpRequest({
      url: `${_urlPrefix}/consent/${id}`,
      method: "DELETE",
      successMessage: "toast.delete.200",
    });
    if (!result) return;
    return result.data;
  }
);

// ==========================================
// Document APIs
// ==========================================

export const createClientDocument = createAsyncThunk(
  "client/createClientDocument",
  async (params) => {
    const { documentFile, ...requestData } = params;
    const data = new FormData();

    if (documentFile) {
      data.append("file", documentFile);
    }

    data.append(
      "content-data",
      new Blob(
        [JSON.stringify(snakecaseKeys(requestData))],
        { type: "application/json" }
      )
    );

    const result = await httpRequest({
      url: `${_urlPrefix}/create/documents`,
      method: "POST",
      headers: { "Content-Type": "multipart/form-data" },
      data: data,
      successMessage: "toast.add.200",
    });
    if (!result) return;
    return result.data;
  }
);

export const updateDocument = createAsyncThunk(
  "client/updateDocument",
  async (params) => {
    const { id, documentFile, ...requestData } = params;
    const data = new FormData();

    if (documentFile) {
      data.append("file", documentFile);
    }

    data.append(
      "content-data",
      new Blob(
        [JSON.stringify(snakecaseKeys(requestData))],
        { type: "application/json" }
      )
    );

    const result = await httpRequest({
      url: `${_urlPrefix}/update/documents/${id}`,
      method: "PUT",
      headers: { "Content-Type": "multipart/form-data" },
      data: data,
      successMessage: "toast.update.200",
    });
    if (!result) return;
    return result.data;
  }
);

const initialState = {
  info: {},
  detail: {},
  list: [],
  activeTab: 0,
  listPaging: {
    queries: {
      page: 1,
      size: 10,
      search_key: "",
      sort_field: "",
      asc_sort: false,
    },
    totalElements: 0,
    totalPages: 0,
  },
};

const client = createSlice({
  name: "client",
  initialState: initialState,
  reducers: {
    clearClientDetail: (client) => {
      client.detail = {};
    },
    setClientListPagingQueries: (state, { payload }) => {
      state.listPaging.queries = { ...state.listPaging.queries, ...payload };
    },
    resetClientListPaging: (state) => {
      state.listPaging = { ...initialState.listPaging };
    },
    setClientActiveTab: (state, { payload }) => {
      state.activeTab = payload;
    },
  },
  extraReducers: {
    [fetchClientDetail.fulfilled]: (state, action) => {
      if (action.payload) {
        let clone = { ...action.payload };
        for (const key in clone.client) {
          if (!clone.client[key]) {
            clone.client[key] = "";
          }
        }
        for (const key in clone.clientDetail) {
          if (!clone.clientDetail[key]) {
            clone.clientDetail[key] = "";
          }
        }
        state.detail = clone;
      }
    },
    [fetchClientListPaging.fulfilled]: (state, { payload }) => {
      if (payload) {
        state.list = payload.content;
        state.listPaging.totalElements = payload.totalElements || 0;
        state.listPaging.totalPages = payload.totalPages || 0;
      }
    },
  },
});

const { reducer, actions } = client;
export const { clearClientDetail, setClientListPagingQueries, resetClientListPaging, setClientActiveTab } = actions;
export default reducer;

export const getClientDetail = createSelector(
  (state) => state.client.detail,
  (client) => client,
);
export const getClientList = createSelector(
  (state) => state.client.list,
  (list) => list,
);
export const getClientListPaging = createSelector(
  (state) => state.client.listPaging,
  (listPaging) => listPaging,
);
export const getClientActiveTab = createSelector(
  (state) => state.client.activeTab,
  (activeTab) => activeTab,
);
